import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Syntax = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">✍️ CSS Syntax</h1>

      <p className="tutorial-intro">
        CSS is made up of rules that tell the browser how to style HTML
        elements. Each rule consists of a <strong>selector</strong> and a{" "}
        <strong>declaration block</strong>. Inside the declaration block, we use
        <strong> properties</strong> and <strong>values</strong>.
      </p>

      {/* Basic Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">📘 Basic Syntax</h2>
        <CodeCopyBlock
          code={`selector {
  property: value;
}`}
        />
        <ul className="tutorial-list">
          <li>
            <strong>Selector</strong>: Targets the HTML element to be styled.
          </li>
          <li>
            <strong>Property</strong>: The style attribute you want to change.
          </li>
          <li>
            <strong>Value</strong>: The setting for the property.
          </li>
        </ul>
      </div>

      {/* Example */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example</h2>
        <CodeCopyBlock
          code={`p {
  color: red;
  font-size: 16px;
}`}
        />
        <p>
          This rule makes all <code>&lt;p&gt;</code> elements appear in red text
          with a font size of 16px.
        </p>

        <div
          style={{
            border: "1px solid #ddd",
            padding: "1rem",
            borderRadius: "8px",
            marginTop: "1rem",
          }}
        >
          <p style={{ color: "red", fontSize: "16px", margin: 0 }}>
            This is a styled paragraph.
          </p>
        </div>
      </div>

      {/* Multiple Declarations */}
      <div className="tutorial-section">
        <h2 className="section-title">📝 Multiple Declarations</h2>
        <p>
          You can include multiple property-value pairs inside one declaration
          block, separated by semicolons.
        </p>
        <CodeCopyBlock
          code={`h1 {
  color: blue;
  text-align: center;
  margin: 20px;
}`}
        />
        <div
          style={{
            background: "#f0f9ff",
            border: "1px solid #ddd",
            padding: "1rem",
            borderRadius: "8px",
            textAlign: "center",
            marginTop: "1rem",
          }}
        >
          <h1 style={{ color: "blue", margin: 0 }}>This is a styled heading</h1>
        </div>
      </div>

      {/* Comments */}
      <div className="tutorial-section">
        <h2 className="section-title">💬 Comments in CSS</h2>
        <p>
          Comments are ignored by browsers and used to explain code. They are
          written between <code>/* ... */</code>.
        </p>
        <CodeCopyBlock
          code={`/* This is a CSS comment */
p {
  color: green;
}`}
        />
      </div>

      {/* Visual Highlight Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #f0fdf4, #bbf7d0)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#166534", marginBottom: "0.5rem" }}>
          🔑 Key Points
        </h2>
        <ul style={{ lineHeight: "1.8", paddingLeft: "1.5rem" }}>
          <li>
            Always end each declaration with a semicolon <code>;</code>.
          </li>
          <li>Use clear selectors to target the right HTML elements.</li>
          <li>Keep CSS readable with indentation and comments.</li>
        </ul>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Now that you know the syntax, let’s move on to{" "}
          <a href="/css/howto" className="learn-more">
            CSS How To
          </a>{" "}
          to learn the different ways of applying CSS to HTML.
        </p>
      </div>
    </div>
  );
};

export default CSS_Syntax;
